import React, { useState, useEffect } from 'react';
import { familyGroupsAPI } from '../services/api';
import { useWeddingContext } from '../context/WeddingContext';
import { useFamily } from '../hooks/useFamily';

interface FamilyGroup {
  groupName: string;
  familyName: string;
  description: string;
  createdDate: string;
  inviteToken: string;
  inviteLink: string;
  members: Array<{
    userId: string;
    email: string;
    displayName: string;
    role: string;
    joinedAt: string;
  }>;
  familySettings: {
    weddingDate?: string;
    venue?: string;
    budget?: number;
    estimatedGuests?: number;
  };
}

interface FamilyGroupManagerProps {
  onFamilyNameUpdated?: () => void;
}

const FamilyGroupManager: React.FC<FamilyGroupManagerProps> = ({ onFamilyNameUpdated }) => {
  const { user } = useWeddingContext();
  const { weddingGroup, isLoading: contextLoading, isOwner } = useFamily(user);
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [copyMessage, setCopyMessage] = useState<string>('');
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [removingMember, setRemovingMember] = useState<string | null>(null);

  // contextからのデータ更新を監視（初期データ読み込みも兼ねる）
  useEffect(() => {
    if (weddingGroup && !contextLoading) {
      const contextFamilyGroups: FamilyGroup[] = [{
        groupName: weddingGroup.groupName || weddingGroup.id,
        familyName: weddingGroup.familyName || '',
        description: `${weddingGroup.familyName || 'ファミリー'}の結婚準備グループ`,
        createdDate: weddingGroup.createdAt,
        inviteToken: weddingGroup.inviteToken || '',
        inviteLink: weddingGroup.inviteLink || '',
        members: weddingGroup.members,
        familySettings: weddingGroup.settings
      }];
      
      // デバッグ情報を出力
      console.log('🔍 FamilyGroupManager DEBUG:', {
        isOwner,
        user: {
          username: user?.username,
          email: user?.email,
          attributes: user?.attributes
        },
        members: weddingGroup.members?.map((m: any) => ({
          userId: m.userId,
          email: m.email,
          role: m.role,
          displayName: m.displayName
        }))
      });
      
      // 既存のデータと比較して、変更がある場合のみ更新
      setFamilyGroups(prevGroups => {
        if (prevGroups.length === 0 || 
            prevGroups[0]?.groupName !== contextFamilyGroups[0]?.groupName ||
            prevGroups[0]?.familyName !== contextFamilyGroups[0]?.familyName ||
            prevGroups[0]?.members?.length !== contextFamilyGroups[0]?.members?.length) {
          return contextFamilyGroups;
        }
        return prevGroups;
      });
    } else if (!contextLoading && !weddingGroup) {
      // contextの読み込みが完了したがweddingGroupがない場合のみAPIを呼び出し
      const loadData = async () => {
        try {
          setLoading(true);
          setError('');
          const response = await familyGroupsAPI.getFamilyGroups();
          setFamilyGroups(response.familyGroups || []);
        } catch (error) {
          console.error('Load family groups error:', error);
          setError('ファミリーグループの読み込みに失敗しました');
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [weddingGroup?.groupName, weddingGroup?.familyName, weddingGroup?.members?.length, contextLoading]); // オブジェクト全体ではなく、必要な値のみを監視

  // 招待リンク生成
  const handleGenerateInviteLink = async (groupName: string) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await familyGroupsAPI.generateInviteToken(groupName);
      
      if (response.inviteLink) {
        // ローカルのfamilyGroups状態を即座に更新
        setFamilyGroups(prevGroups => 
          prevGroups.map(group => 
            group.groupName === groupName 
              ? { ...group, inviteLink: response.inviteLink, inviteToken: response.inviteToken }
              : group
          )
        );
      }
    } catch (error: any) {
      console.error('Generate invite link error:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('招待リンクの生成に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  // ファミリーアカウント名更新機能
  const handleUpdateFamilyName = async (groupName: string, newFamilyName: string) => {
    try {
      setLoading(true);
      setError('');
      
      await familyGroupsAPI.updateFamilyName(groupName, newFamilyName);
      
      setCopyMessage('ファミリーアカウント名を更新しました！');
      setTimeout(() => setCopyMessage(''), 3000);
      
      // 編集状態をリセット
      setEditingGroup(null);
      setEditingName('');
      
      // ファミリーアカウント名更新成功を親に通知（ヘッダー更新のため）
      if (onFamilyNameUpdated) {
        onFamilyNameUpdated();
      }
    } catch (error: any) {
      console.error('Update family name error:', error);
      setError('ファミリーアカウント名の更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 編集開始
  const startEditing = (groupName: string, currentName: string) => {
    setEditingGroup(groupName);
    setEditingName(currentName);
  };

  // 編集キャンセル
  const cancelEditing = () => {
    setEditingGroup(null);
    setEditingName('');
  };

  // メンバー削除
  const handleRemoveMember = async (groupName: string, memberUserId: string, memberEmail: string) => {
    if (!window.confirm(`${memberEmail} をファミリーアカウントから削除しますか？\n\nこの操作は取り消せません。削除されたメンバーは再度招待リンクから参加する必要があります。`)) {
      return;
    }

    try {
      setRemovingMember(memberUserId);
      setError('');
      
      await familyGroupsAPI.removeMember(groupName, memberUserId);
      
      setCopyMessage(`${memberEmail} をファミリーアカウントから削除しました。`);
      setTimeout(() => setCopyMessage(''), 3000);
      
      // ローカル状態の更新はcontextの自動更新に任せる
    } catch (error: any) {
      console.error('Remove member error:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('メンバーの削除に失敗しました');
      }
    } finally {
      setRemovingMember(null);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        {/* ヘッダー */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800">👫 ファミリーアカウント管理</h2>
        </div>

        {/* コピー成功メッセージ */}
        {copyMessage && (
          <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-xs sm:text-sm">
            <div style={{ whiteSpace: 'pre-line' }}>
              ✅ {copyMessage}
            </div>
          </div>
        )}

        {/* エラー表示 */}
        {error && (
          <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs sm:text-sm">
            {error}
          </div>
        )}

        {/* ローディング */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            <p className="mt-2 text-gray-600">処理中...</p>
          </div>
        )}

        {/* ファミリーグループ一覧 */}
        {!loading && (
          <div className="space-y-4">
            {familyGroups.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-gray-500">
                <p className="text-base sm:text-lg mb-2">💍 ファミリーアカウントが見つかりません</p>
                <p className="text-sm sm:text-base mb-4 px-2">アカウント作成時にファミリーアカウントが正常に設定されませんでした。</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 max-w-sm sm:max-w-md mx-auto">
                  <h3 className="font-semibold text-red-800 mb-2 text-sm sm:text-base">⚠️ サポートが必要です</h3>
                  <p className="text-xs sm:text-sm text-red-700 text-left">
                    この状態は通常発生しません。新しいアカウントを作成するか、サポートにお問い合わせください。
                  </p>
                </div>
              </div>
            ) : (
              familyGroups.map((group, index) => (
                <div key={group.groupName || index} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      {editingGroup === group.groupName ? (
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm sm:text-base min-w-0"
                            placeholder="ファミリーアカウント名を入力"
                          />
                          <button
                            onClick={() => handleUpdateFamilyName(group.groupName, editingName)}
                            className="px-2 py-1 bg-green-500 text-white text-xs sm:text-sm rounded hover:bg-green-600 transition-colors whitespace-nowrap"
                            disabled={!editingName.trim()}
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-2 py-1 bg-gray-500 text-white text-xs sm:text-sm rounded hover:bg-gray-600 transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-800 break-words">
                        💕 {group.familyName || group.groupName.replace('family_', '')}
                      </h3>
                          <button
                            onClick={() => startEditing(group.groupName, group.familyName || group.groupName.replace('family_', ''))}
                            className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200 transition-colors whitespace-nowrap"
                            title="ファミリーアカウント名を編集"
                          >
                            ✏️ 編集
                          </button>
                        </div>
                      )}
                      {group.description && (
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">{group.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2 sm:mt-0">
                      <button
                        onClick={() => handleGenerateInviteLink(group.groupName)}
                        className="px-2 sm:px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded hover:bg-blue-600 transition-colors whitespace-nowrap"
                        disabled={loading}
                      >
                        招待リンク生成
                      </button>
                    </div>
                  </div>
                  
                  {/* メンバー一覧 */}
                  {group.members && group.members.length > 0 && (
                    <div className="mt-3 sm:mt-4">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        👥 メンバー ({group.members.length}人)
                        {isOwner && (
                          <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                            👑 オーナー権限あり
                          </span>
                        )}
                        {!isOwner && (
                          <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            一般メンバー
                          </span>
                        )}
                      </h4>
                      <div className="space-y-1 sm:space-y-2">
                        {group.members.map((member, memberIndex) => (
                          <div key={member.userId || memberIndex} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-2 sm:p-3 rounded border gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-xs sm:text-sm font-medium text-gray-800 break-all">
                                {member.displayName || member.email}
                              </span>
                              <span className={`px-1 sm:px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                                member.role === 'owner' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {member.role === 'owner' ? '👑 オーナー' : '👤 メンバー'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-gray-500 whitespace-nowrap">
                                参加: {new Date(member.joinedAt).toLocaleDateString('ja-JP')}
                              </div>
                              {/* オーナーのみ表示：自分以外のメンバーを削除するボタン */}
                              {isOwner && 
                               member.userId !== user?.username && 
                               member.email !== user?.username &&
                               member.userId !== user?.email &&
                               member.email !== user?.email &&
                               member.userId !== user?.sub &&
                               member.email !== user?.sub &&
                               !(user?.attributes && member.email === user.attributes.email) &&
                               !(user?.attributes && member.userId === user.attributes.sub) &&
                               member.role !== 'owner' && (
                                <button
                                  onClick={() => handleRemoveMember(group.groupName, member.userId, member.email)}
                                  disabled={removingMember === member.userId}
                                  className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                  title={`${member.email} を削除`}
                                >
                                  {removingMember === member.userId ? '削除中...' : '🗑️ 削除'}
                                </button>
                              )}
                              {/* デバッグ用：条件が満たされない場合の表示 */}
                              {!isOwner && (
                                <div className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">
                                  オーナー権限なし
                                </div>
                              )}
                              {isOwner && (
                                member.userId === user?.username || 
                                member.email === user?.username ||
                                member.userId === user?.email ||
                                member.email === user?.email ||
                                member.userId === user?.sub ||
                                member.email === user?.sub ||
                                (user?.attributes && member.email === user.attributes.email) ||
                                (user?.attributes && member.userId === user.attributes.sub)
                              ) && (
                                <div className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">
                                  自分自身
                                </div>
                              )}
                              {isOwner && member.userId !== user?.username && member.role === 'owner' && (
                                <div className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">
                                  他のオーナー
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 招待リンク情報 */}
                  {group.inviteLink && (
                    <div className="mt-3 sm:mt-4 bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3">
                      <h4 className="text-xs sm:text-sm font-medium text-blue-800 mb-2">🔗 招待リンク</h4>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={group.inviteLink}
                          readOnly
                          className="flex-1 px-2 py-1 text-xs sm:text-sm bg-white border border-blue-300 rounded min-w-0 cursor-pointer"
                          onClick={(e) => {
                            // クリックで全選択
                            const target = e.target as HTMLInputElement;
                            target.select();
                          }}
                          title="クリックで全選択"
                        />
                        <button
                          onClick={async () => {
                            try {
                              // 現代的なClipboard APIを試行
                              if (navigator.clipboard && window.isSecureContext) {
                                await navigator.clipboard.writeText(group.inviteLink);
                                setCopyMessage('招待リンクをコピーしました！');
                              } else {
                                // フォールバック: 古いブラウザや非HTTPS環境用
                                const textArea = document.createElement('textarea');
                                textArea.value = group.inviteLink;
                                textArea.style.position = 'fixed';
                                textArea.style.left = '-999999px';
                                textArea.style.top = '-999999px';
                                document.body.appendChild(textArea);
                                textArea.focus();
                                textArea.select();
                                
                                try {
                                  document.execCommand('copy');
                                  setCopyMessage('招待リンクをコピーしました！');
                                } catch (err) {
                                  console.warn('execCommand copy failed:', err);
                                  setCopyMessage('コピーに失敗しました。リンクを手動で選択してコピーしてください。');
                                }
                                
                                document.body.removeChild(textArea);
                              }
                              setTimeout(() => setCopyMessage(''), 3000);
                            } catch (error) {
                              console.warn('All clipboard methods failed:', error);
                              setCopyMessage('コピー機能が利用できません。リンクを手動で選択してコピーしてください。');
                              setTimeout(() => setCopyMessage(''), 5000);
                            }
                          }}
                          className="px-2 sm:px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded hover:bg-blue-600 transition-colors whitespace-nowrap"
                        >
                          📋 コピー
                        </button>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">パートナーにこのリンクを送信して、ファミリーアカウントに招待しましょう。</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyGroupManager; 